import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Table, Button } from "reactstrap";
import { CustomButton } from "../Common/Components/Button/CustomButton";
import { ADMIN_PATHS } from "../config";
import { useLocation, useNavigate } from "react-router-dom";
import PersonsAgiReportModel from "./Models/PersonsAgiReportModel";
import SortableList from "../Common/Components/Lists/SortableList";
import useApiCallService from "../Common/Helpers/ApiCall";
import { CustomReload } from "../AppSystem/Helpers/CustomReload";
import { useHeader } from "../Auth/Components/Nav/HeaderContext";
import { CheckMobile } from "../AppSystem/Helpers/CheckMobile";

const STORAGE_KEY = 'reportTaxAuthorityInfo';

export function AdminReportTaxAuthorityInfo() {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const isMobile = CheckMobile(998);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle('Sammanställning');
        setPageTitleKey('sidebar.taxAuthorityInfo');
        setOnBack(() => navigate(ADMIN_PATHS.ADMIN_REPORT_TAX));
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);
    
    const getInitialData = () => {
        // 1. First, try to get data from location state
        if (location.state?.idItem) {
            return {
                idItem: location.state.idItem,
                markdownValue: location.state.markdownValue || '',
                fileName: location.state.fileName || `AGI_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.xml`
            };
        }

        // 2. If no state, try to get it from sessionStorage
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }

        // 3. As a last resort, get it from URL
        return {
            idItem: searchParams.get('id') || '',
            markdownValue: '',
            fileName: `AGI_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.xml`
        };
    };
    
    const initialData = getInitialData();
    const idItem = initialData.idItem;
    const [markdownValue] = useState(initialData.markdownValue);
    const [fileName] = useState(initialData.fileName);

    // Guardar en sessionStorage cuando tengamos datos válidos
    useEffect(() => {
        if (idItem && markdownValue) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                idItem,
                markdownValue,
                fileName
            }));
        }
    }, [idItem, markdownValue, fileName]);

    // Limpiar sessionStorage al salir de la vista
    useEffect(() => {
        return () => {
            if (!window.location.pathname.includes('admin/report-tax-info')) {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        };
    }, []);

    const [summary, setSummary] = useState({
        agId: '',
        period: '',
        avgift: '',
        skatt: ''
    });

    useEffect(() => {
        const loadData = async () => {
            if (!markdownValue) return;
            
            const lines = markdownValue.split('\n').map(line => line.trim());

            let currentPerson = {};
            const persons = [];
            const tempSummary = {
                agId: '',
                period: '',
                avgift: '',
                skatt: ''
            };

            lines.forEach((line) => {
                const match = line.match(/-\s+\*\*(.+?)\*\*:\s*(.+)/);
                if (match) {
                    const key = match[1].trim();
                    const value = match[2].trim();

                    if (key === 'AgRegistreradId' && !tempSummary.agId) tempSummary.agId = value;
                    if (key === 'RedovisningsPeriod' && !tempSummary.period) tempSummary.period = value;
                    if (key === 'SummaArbAvgSlf') tempSummary.avgift = value;
                    if (key === 'SummaSkatteavdr') tempSummary.skatt = value;

                    if (key === 'BetalningsmottagarId') currentPerson.pid = value;
                    if (key === 'RedovisningsPeriod') currentPerson.period = value;
                    if (key === 'KontantErsattningUlagAG') currentPerson.agUnderlag = value;
                    if (key === 'AvdrPrelSkatt') currentPerson.tax = value;
                    if (key === 'Bilersattning') currentPerson.bil = value === 'true' ? 'Ja' : '';

                    if (key === 'Bilersattning') {
                        persons.push({ ...currentPerson });
                        currentPerson = {};
                    }
                }
            });

            setSummary(tempSummary);
        };

        loadData();
    }, [markdownValue]);

    const DownloadFile = async (id, fileName) => {
        let url = `/file?id=${id}`        
        const apiCallOptions = {
            method: "get",
            url: url,
            dataTypes: 'blob',
            responseType: 'blob'
        };
        try {
            await ApiCall(apiCallOptions, (response) => handleDownloadApiResponse(response, fileName));

        } catch (error) {
            console.log(error);
        }
    };

    const handleDownloadApiResponse  =  (response, name) => {
        let file;
        if (response instanceof Blob) {
            file = response;
            
        } else {
            file = new Blob([response]);
        }
        let fileName = name;

        if (!name || name.trim() === "") {
            fileName = "file.pdf";
        } else {
            fileName = name;
        }

        const urlToFile = URL.createObjectURL(file);

        const link = document.createElement('a');
        link.href = urlToFile;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        /*
        setTimeout(() => {
            CustomReload();
        }, 2000);
        */
    }

    const renderActionColumn = (item) => {
        const isDisabled = !item.uid;

        return (
            <div className="d-flex gap-2">
                <button
                    className="rounded me-2 py-2 border-0 text-green bg-green-light p-custom-button position-relative class-approve"
                    disabled={isDisabled}
                    onClick={() => {
                        if (!isDisabled) {
                            navigate(`${ADMIN_PATHS.ADMIN_USER_INFO}?id=${item.uid}`, {
                                state: {
                                    from: "tax-authority-report",
                                    markdownValue: markdownValue,
                                    idItem: idItem
                                }
                            });
                        }
                    }}
                >
                    <i className="bx bx-show pb-custom align-middle size-icon-custom-button" />
                    <div className="tooltip-custom-button">
                        {t('button.info')}
                    </div>
                </button>

                {item.salarySheetFileId && (
                    <CustomButton 
                        icon="bx bx-down-arrow-alt pb-1" 
                        iconColor="#157553" 
                        hoverIconColor='white' 
                        hoverButtonColor='#A3C2B8' 
                        iconSize={4}  
                        buttonColor="#cde0da"  
                        tooltipText='Nedladdningar' 
                        customClasses="rounded" 
                        buttonBorder={false} 
                        customClassesTooltip="tooltip-custom-button-table"
                        id={item.id}
                        onClick={() => {
                            DownloadFile(item.salarySheetFileId, item.salarySheetFileName);
                        }}
                    />
                )}
            </div>
        );
    };

    const columns = ['pid', 'period', 'agUnderlag', 'framraknadAG', 'tax', 'andelStyrelsearvode', 'bil'];

    const listOptions = {
        "model": PersonsAgiReportModel,
        'columns': columns,
        "actions": {
            "actionColumn" : renderActionColumn,
        },
        "exportIsAvailable": true,
        "urlGetListItemsToExport": "/export-data/admin/search-tax-authority-info/filter?itemId=" + idItem,
        "nameFile": 'PersonsAgiReport',
        "ignoreFields" : columns,
        "modelType" : "TaxAuthorityInfo",
        "filterTestIsAvailable": true,
        "urlGetSearch" : "/export-data/admin/search-tax-authority-info/filter?itemId=" + idItem,
        "ignoreUrlParams": true
    };

    return (
        <>
            {!isMobile && (
                <Col lg={1} className="px-0">
                    <CustomButton
                        text={t('button.back')}
                        icon={'bx bx-chevron-left'}
                        buttonSize={'small'}
                        customClasses={'ms-3 mb-3'}
                        onClick={() => navigate(ADMIN_PATHS.ADMIN_REPORT_TAX)}
                    />
                </Col>
            )}

            <Col lg={12} className="px-0 mb-5">
                <div className="card-backend p-5">
                    <div className="mb-5">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h2>Sammanställning</h2>
                            <Button 
                                className="bg-green-light text-green border-0 position-relative" 
                                onClick={() => {
                                    DownloadFile(idItem, fileName);
                                }}
                            >
                                <i className="bx bx-down-arrow-alt fs-5 align-middle pb-1" />
                                <span className="ms-2">Hämta AGI-fil</span>
                                <div className="tooltip-custom-button-table">
                                    Ladda ner AGI-fil
                                </div>
                            </Button>
                        </div>
                        <div className="table-responsive">
                            <Table bordered className="tax-summary-table">
                                <tbody>
                                    <tr>
                                        <th className="bg-light text-start w-50">Organisationsnummer</th>
                                        <td className="text-start">{summary.agId || '-'}</td>
                                    </tr>
                                    <tr>
                                        <th className="bg-light text-start">Redovisningsperiod</th>
                                        <td className="text-start">{summary.period || '-'}</td>
                                    </tr>
                                    <tr>
                                        <th className="bg-light text-start">Total arbetsgivaravgift</th>
                                        <td className="text-start">{summary.avgift || '-'}</td>
                                    </tr>
                                    <tr>
                                        <th className="bg-light text-start">Totalt innehållen skatt</th>
                                        <td className="text-start">{summary.skatt || '-'}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    <h2 className="pb-2 rokkitt-font">Personer</h2>
                    <SortableList
                        listOptions={listOptions}
                    />
                </div>
            </Col>
        </>
    );
}